/**
 * SOP markdown -> guided steps: the client port of the backend `SopSteps.py`
 * parser plus `Knowledge._clean_steps`.
 *
 * The server owns the real derivation (`preview_article_steps`), so this exists
 * for the two places that have no saved article to ask about: the mock backend
 * and "Generate steps from body" on an article that has never been saved. Ids
 * become step ids on save, so the two implementations must stay identical.
 */

import { STEP_MAX_MEDIA, normalizeMedia } from './mediaLinks';

export { STEP_MAX_MEDIA } from './mediaLinks';
export const MAX_STEPS = 60;
export const MAX_TITLE = 120;
export const MAX_INSTRUCTION = 4000;
const FALLBACK_TITLE_CHARS = 80;

// The space after the hashes is optional: `###1. Title` is written by hand often
// enough, and requiring it parsed such an article to nothing.
const HEADING_RE = /^(#{1,6})\s*(.*?)\s*#*\s*$/;
// Trailing space optional so a bare `### 1.` empties out and the step is dropped
// instead of becoming a step titled "1.".
const NUMBER_PREFIX_RE = /^\(?\d+\s*[.)]\s*/;
const NUMBERED_ITEM_RE = /^(\d+)[.)]\s+(.*)$/;
const EMPHASIS_RE = /\*\*|__|`/g;
const SENTENCE_END_RE = /[.!?](\s|$)/;
const SLUG_STRIP_RE = /[^a-z0-9]+/g;
const VALID_SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

export const STEP_EVIDENCE_METHODS = [
    { value: 'confirm', label: 'Confirm' },
    { value: 'photo', label: 'Photo' },
];

export const STEP_EVIDENCE_DEFAULTS = {
    method: 'confirm',
    photoPolicy: 'required',
    aiCheck: true,
    photoPrompt: '',
    checkPrompt: '',
    sampleRate: 1,
};

const stripEmphasis = (text) => String(text || '').replace(EMPHASIS_RE, '');

const trimDashes = (text) => text.replace(/^-+/, '').replace(/-+$/, '');

/** Lowercase [a-z0-9-] id for a step title. */
export const slug = (text, fallback = 'step') => {
    const base = trimDashes(String(text || '').trim().toLowerCase().replace(SLUG_STRIP_RE, '-'));
    return trimDashes((base || fallback).slice(0, 60)) || fallback;
};

export const isSlug = (value) =>
    typeof value === 'string' && value.length > 0 && value.length <= 60 && VALID_SLUG_RE.test(value);

/** Suffix -2, -3 … until the id is free within one article. */
export const uniqueSlug = (candidate, used) => {
    let out = candidate;
    let n = 2;
    while (used.has(out)) {
        out = `${candidate}-${n}`;
        n += 1;
    }
    used.add(out);
    return out;
};

const cleanTitle = (text) =>
    stripEmphasis(String(text || '').trim().replace(NUMBER_PREFIX_RE, '')).trim().slice(0, MAX_TITLE);

const headings = (lines) => {
    const out = [];
    lines.forEach((line, index) => {
        const match = HEADING_RE.exec(line);
        if (match) out.push({ index, level: match[1].length, text: match[2].trim() });
    });
    return out;
};

/** Line range of the `## Procedure` section, or null when there is none. */
const procedureRange = (heads, totalLines) => {
    for (let i = 0; i < heads.length; i += 1) {
        const head = heads[i];
        const label = stripEmphasis(head.text).trim().toLowerCase().replace(/:+$/, '');
        if (head.level !== 2 || !label.startsWith('procedure')) continue;
        const next = heads.slice(i + 1).find((h) => h.level <= 2);
        return { start: head.index, end: next ? next.index : totalLines };
    }
    return null;
};

/** Markdown from just after a heading to the next heading of any level. */
const bodyBetween = (lines, start, heads) => {
    const next = heads.find((h) => h.index > start);
    return lines.slice(start + 1, next ? next.index : lines.length).join('\n').trim().slice(0, MAX_INSTRUCTION);
};

const startsWithSpace = (line) => /^\s/.test(line);

/** Top-level `1. text` items with their indented continuation lines. */
const numberedItems = (lines) => {
    const items = [];
    let current = null;
    lines.forEach((line) => {
        const match = startsWithSpace(line) ? null : NUMBERED_ITEM_RE.exec(line);
        if (match) {
            current = [match[2].trim()];
            items.push(current);
            return;
        }
        if (current === null || !line.trim()) return;
        if (startsWithSpace(line)) current.push(line.trim());
        else current = null;
    });
    return items.map((item) => item.join('\n').trim()).filter(Boolean);
};

const firstSentence = (text) => {
    let line = stripEmphasis(String(text || '').split('\n')[0]).trim();
    const match = SENTENCE_END_RE.exec(line);
    if (match) line = line.slice(0, match.index).trim();
    return line.slice(0, FALLBACK_TITLE_CHARS).trim();
};

const derivedStep = (title, instruction, order, used) => ({
    id: uniqueSlug(slug(title, `step-${order + 1}`), used),
    order,
    title: title.slice(0, MAX_TITLE),
    instruction: String(instruction || '').slice(0, MAX_INSTRUCTION),
    required: true,
});

/**
 * Steps parsed out of an SOP body, or [] when nothing parses. Preference order:
 * `###` headings inside `## Procedure`, then any `###` heading, then top-level
 * numbered list items.
 */
export const deriveStepsFromBody = (body) => {
    const lines = String(body || '').replace(/\r\n/g, '\n').split('\n');
    const heads = headings(lines);
    const section = procedureRange(heads, lines.length);

    let candidates = section
        ? heads.filter((h) => h.level === 3 && h.index > section.start && h.index < section.end)
        : [];
    if (!candidates.length) candidates = heads.filter((h) => h.level === 3);

    const used = new Set();
    const steps = [];
    candidates.slice(0, MAX_STEPS).forEach((head) => {
        const title = cleanTitle(head.text);
        if (!title) return;
        steps.push(derivedStep(title, bodyBetween(lines, head.index, heads), steps.length, used));
    });
    if (steps.length) return steps;

    numberedItems(lines).slice(0, MAX_STEPS).forEach((text) => {
        const title = cleanTitle(firstSentence(text));
        if (!title) return;
        steps.push(derivedStep(title, text.trim(), steps.length, used));
    });
    return steps;
};

export const normalizeStepEvidence = (raw) => {
    const source = raw && typeof raw === 'object' ? raw : {};
    const rate = Number(source.sampleRate);
    return {
        method: source.method === 'photo' ? 'photo' : 'confirm',
        photoPolicy: source.photoPolicy === 'optional' ? 'optional' : 'required',
        aiCheck: source.aiCheck === undefined || source.aiCheck === null ? true : Boolean(source.aiCheck),
        photoPrompt: String(source.photoPrompt || '').trim().slice(0, 1000),
        checkPrompt: String(source.checkPrompt || '').trim().slice(0, 1000),
        sampleRate: Number.isFinite(rate) ? Math.min(1, Math.max(0, rate)) : 1,
    };
};

/** Mirror of `Knowledge._clean_steps`: slug ids unique per article, titles required, up to three video links. */
export const normalizeSopSteps = (raw) => {
    if (!Array.isArray(raw)) return [];
    const used = new Set();
    const out = [];
    raw.slice(0, MAX_STEPS).forEach((step) => {
        if (!step || typeof step !== 'object') return;
        const title = String(step.title || '').trim().slice(0, MAX_TITLE);
        if (!title) return;
        const candidate = isSlug(step.id) ? step.id : slug(title, `step-${out.length + 1}`);
        const order = Number.parseInt(step.order, 10);
        out.push({
            id: uniqueSlug(candidate, used),
            order: Number.isFinite(order) ? order : out.length,
            title,
            instruction: String(step.instruction || '').slice(0, MAX_INSTRUCTION),
            required: step.required === undefined ? true : Boolean(step.required),
            evidence: normalizeStepEvidence(step.evidence),
            media: normalizeMedia(step.media, STEP_MAX_MEDIA),
        });
    });
    return out;
};

export const emptySopStep = (order = 0) => ({
    id: '',
    order,
    title: '',
    instruction: '',
    required: true,
    evidence: { ...STEP_EVIDENCE_DEFAULTS },
    media: [],
});
